import { get } from "lodash";
import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useMutation } from "react-query";

import useApi from "hooks/useApi";
import useApp from "hooks/useApp";

const usePostForm = (
  { formUrl, formData, validationSchema, afterSubmit },
  checkLicense
) => {
  const api = useApi();
  const app = useApp();
  const { licenseData, setLicenseData } = app;
  const [errorMsg, setErrorMsg] = useState(null);
  const [data, setData] = useState(null);
  const [isLicenseKeyValid, setIsLicenseKeyValid] = useState(false);
  const [isLicenseExpired, setIsLicenseExpired] = useState(false);
  const [areSMTPDetailsNeeded, setAreSMTPDetailsNeeded] = useState(false);
  const [validationInProgress, setValidationInProgress] = useState(false);
  const [fingerprint, setFingerprint] = useState("");
  const [connection, setConnection] = useState("ONLINE");
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  async function validateSMTPDetails(formData) {
    if (!validationInProgress) setValidationInProgress(true);
    const smtpPostUrl = "auth/add_admin";
    const registerUser = get(formData, "registerUser");
    const registerEmail = get(formData, "registerEmail");
    const registerPassword = get(formData, "registerPassword");
    const mailUsername = get(formData, "mailUsername");
    const mailPassword = get(formData, "mailPassword");
    const mailServer = get(formData, "mailServer");
    const mailPort = get(formData, "mailPort");
    const mailUseTLS = get(formData, "mailUseTLS", false);
    const mailUseSSL = get(formData, "mailUseSSL", false);
    const mailDefaultSender = get(formData, "mailDefaultSender");
    const smtpPostObj = {};
    if (
      registerUser &&
      registerEmail &&
      registerPassword
    ) {
      smtpPostObj.user = registerUser;
      smtpPostObj.email = registerEmail;
      smtpPostObj.password = registerPassword;
      smtpPostObj.mail_username = mailUsername;
      smtpPostObj.mail_password = mailPassword;
      smtpPostObj.mail_server = mailServer;
      smtpPostObj.mail_port = mailPort;
      smtpPostObj.mail_use_tls = mailUseTLS;
      smtpPostObj.mail_use_ssl = mailUseSSL;
      smtpPostObj.mail_default_sender = mailDefaultSender;
    }
    return api
      .post(smtpPostUrl, smtpPostObj)
      .then((res) => {
        setValidationInProgress(false);
        setAreSMTPDetailsNeeded(false);
        return Promise.resolve(res);
      })
      .catch((e) => {
        const status = get(e, "response.status", 400);
        setValidationInProgress(false);
        setAreSMTPDetailsNeeded(true);
        setErrorMsg(null);
        if (status === 400) {
          e.response.data = {
            message: e.response.data,
          };
          return Promise.reject(e);
        }
        return Promise.resolve(e);
      });
  }

  async function validationCheckSuccess(res, validateSMTP) {
    const data = get(res, "data.data", {});
    setLicenseData(data);
    setIsLicenseExpired(false);
    setIsLicenseKeyValid(true);
    setErrorMsg(null);
    if (validateSMTP) {
      return validateSMTPDetails().catch(errorHandler);
    } else {
      setValidationInProgress(false);
      return Promise.resolve(res);
    }
  }

  function validationCheckError(e, isInitialValidation) {
    setIsLicenseKeyValid(false);
    setValidationInProgress(false);
    if (!isInitialValidation) {
      const data = get(e, "response.data");
      if (data?.data?.status === "EXPIRED") {
        data.message = "License is expired. Please contact administrator.";
        setIsLicenseExpired(true);
      } else {
        const connection = get(data, "data.connection", "ONLINE");
        const fingerprint = get(data, "data.fingerprint", "");
        setConnection(connection);
        setFingerprint(fingerprint);
        setIsLicenseExpired(false);
      }
      setErrorMsg(data?.message || "Validation failed");
    }
    return Promise.reject(e);
  }

  async function validateLicense(formData, fieldName, isInitialValidation = false) {
    setValidationInProgress(true);
    let postUrl = "license/validate_license";
    const postObj = {};
    const licenseKey = get(formData, "licenseKey");
    const ipAddress = get(formData, "ipAddress");
    const dbUrl = get(formData, "dbUrl");
    if (licenseKey && ipAddress && dbUrl) {
      postObj.license_key = licenseKey;
      postObj.ip_address = ipAddress;
      postObj.db_url = dbUrl;
    }
    if (fieldName) {
      postUrl += `/${fieldName}`;
    }
    return api
      .post(postUrl, postObj)
      .then((res) => validationCheckSuccess(res, checkLicense))
      .catch((e) => validationCheckError(e, isInitialValidation));
  }

  function errorHandler(error) {
    let errMsg = get(error, "response.data.message");
    if (!errMsg) {
      errMsg = get(error, "response.data");
    }
    if (!errMsg) {
      errMsg = "Unable to send request";
    }
    setErrorMsg(errMsg);
  }

  useEffect(() => {
    if (checkLicense) {
      validateLicense({}, null, true)
        .then((res) => res)
        .catch(() => {
        })
        .finally(() => {
          setIsInitialLoad(false);
        });
    }
  }, []);

  function saveFormData(postData) {
    if (formUrl === "account/changepassword") {
      return api.put(formUrl, postData).then((res) => res.data);
    } else {
      let updatedPostData = postData;
      if (checkLicense) {
        if (!isLicenseKeyValid) {
          return validateLicense(postData).then((res) => res.data);
        }
        if (areSMTPDetailsNeeded) {
          return validateSMTPDetails(postData).then((res) => res.data);
        }
        updatedPostData = {
          username: postData.username,
          password: postData.password,
        };
      }
      return api.post(formUrl, updatedPostData).then((res) => res.data);
    }
  }

  const mutation = useMutation(saveFormData, {
    retry: false,
    onSuccess: (data) => {
      setData(data);
      if (typeof afterSubmit === "function") {
        afterSubmit(data);
      }
      setErrorMsg(null);
    },
    onError: errorHandler,
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: formData,
    validationSchema: validationSchema,
    onSubmit: async (validatedFormData) => {
      setErrorMsg(null);
      mutation.mutate(validatedFormData);
    },
  });

  const isFieldValid = (name) =>
    !!(formik.touched[name] && formik.errors[name]);
  const getFieldError = (name) => {
    return (
      isFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    );
  };

  const getErrorClass = (name) => {
    return { "p-invalid": isFieldValid(name) };
  };

  const vals = {
    data,
    loading: mutation.isLoading,
    isLicenseKeyValid,
    validationInProgress,
    setValidationInProgress,
    areSMTPDetailsNeeded,
    connection,
    fingerprint,
    errorMsg,
    validateLicense,
    isLicenseExpired,
    errorHandler,
    validationCheckSuccess,
    validationCheckError,
    setErrorMsg,
    isFieldValid,
    getFieldError,
    getErrorClass,
    formik,
  };
  return vals;
};

export default usePostForm;
